import { useEffect, useRef, useState, useCallback } from "react";
import { subscribeToasts } from "./toastStore";
import toasterLogo from "../../assets/images/toaster-logo.jpg";
import "./CustomToaster.scss";

// Small status badges drawn on the corner of the brand logo per type.
const Badge = ({ type }) => {
  if (type === "success") {
    return (
      <span className="ctoast__badge">
        <svg viewBox="0 0 24 24">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </span>
    );
  }
  if (type === "error") {
    return (
      <span className="ctoast__badge">
        <svg viewBox="0 0 24 24">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </span>
    );
  }
  if (type === "warning") {
    return (
      <span className="ctoast__badge">
        <svg viewBox="0 0 24 24">
          <line x1="12" y1="8" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12" y2="17" />
        </svg>
      </span>
    );
  }
  return (
    <span className="ctoast__badge">
      <svg viewBox="0 0 24 24">
        <line x1="12" y1="11" x2="12" y2="16" />
        <line x1="12" y1="8" x2="12" y2="8" />
      </svg>
    </span>
  );
};

const ToastCard = ({ toast, onDismiss }) => {
  const { id, type, title, message, duration, onClick } = toast;
  const [leaving, setLeaving] = useState(false);
  const timerRef = useRef(null);
  const startRef = useRef(0);
  const remainingRef = useRef(duration);

  // Trigger the exit animation, then remove after it finishes.
  const beginClose = useCallback(() => {
    setLeaving(true);
    window.setTimeout(() => onDismiss(id), 320); // matches ctoast-out duration
  }, [id, onDismiss]);

  // Auto-dismiss timer with hover pause/resume (0 = sticky).
  const clearTimer = () => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };
  const startTimer = useCallback(
    (ms) => {
      clearTimer();
      if (!ms || ms <= 0) return;
      startRef.current = Date.now();
      timerRef.current = window.setTimeout(beginClose, ms);
    },
    [beginClose],
  );

  useEffect(() => {
    remainingRef.current = duration;
    startTimer(duration);
    return clearTimer;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMouseEnter = () => {
    if (!duration || duration <= 0) return;
    remainingRef.current -= Date.now() - startRef.current;
    clearTimer();
  };
  const handleMouseLeave = () => {
    if (!duration || duration <= 0) return;
    startTimer(Math.max(remainingRef.current, 400));
  };

  const handleCardClick = () => {
    if (!onClick) return;
    try {
      onClick();
    } catch (e) {
      /* a broken handler must not break the toast */
    }
    beginClose();
  };

  return (
    <div
      className={`ctoast ctoast--${type} ${leaving ? "ctoast-leaving" : ""}`}
      role="alert"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={onClick ? handleCardClick : undefined}
      style={onClick ? { cursor: "pointer" } : undefined}
    >
      <div className="ctoast__logo-wrap">
        <img className="ctoast__logo" src={toasterLogo} alt="" />
        <Badge type={type} />
      </div>

      <div className="ctoast__body">
        {title ? <p className="ctoast__title">{title}</p> : null}
        {message ? <p className="ctoast__message">{message}</p> : null}
      </div>

      <button
        className="ctoast__close"
        aria-label="Close"
        onClick={(e) => {
          e.stopPropagation();
          beginClose();
        }}
      >
        <svg viewBox="0 0 24 24">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>

      {duration > 0 && !leaving ? (
        <span
          className="ctoast__progress"
          style={{ animationDuration: `${duration}ms` }}
        />
      ) : null}
    </div>
  );
};

/**
 * Mount ONCE near the app root. Subscribes to the toast store and renders the
 * top-right stack. No provider/context needed — the store is a module singleton.
 */
const CustomToaster = () => {
  const [toasts, setToasts] = useState([]);

  const remove = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  useEffect(() => {
    const unsubscribe = subscribeToasts((action) => {
      if (action.type === "add") {
        setToasts((prev) => {
          // Guard against duplicate ids (e.g. rapid double dispatch).
          if (prev.some((t) => t.id === action.toast.id)) return prev;
          // Cap the stack so a burst never floods the screen.
          const next = [...prev, action.toast];
          return next.length > 5 ? next.slice(next.length - 5) : next;
        });
      } else if (action.type === "remove") {
        setToasts((prev) => prev.filter((t) => t.id !== action.id));
      } else if (action.type === "clear") {
        setToasts([]);
      }
    });
    return unsubscribe;
  }, []);

  if (toasts.length === 0) return null;

  return (
    <div className="ctoast-viewport" aria-live="polite" aria-atomic="false">
      {toasts.map((t) => (
        <ToastCard key={t.id} toast={t} onDismiss={remove} />
      ))}
    </div>
  );
};

export default CustomToaster;
